import * as React from "react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Input } from "@/components/ui/Input"
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/Table"
import type { Market } from "./lib/types"
import IncomingOrderRow from "./components/IncomingOrderRow"
import { matchesSearch, type IncomingOrderLine } from "./lib/incoming"

interface Props {
  today: string
  markets: Market[]
  lines: IncomingOrderLine[]
}

const HEAD = "h-8 px-2 py-0 text-xs"
const COLUMNS = ["Order", "Product", "Market", "Qty", "ETD", "ETA", "Status", "Warehouse note", "Stock impact"]

// Every order line on its way, all markets, soonest arrival first. Read-only:
// the lines come from the buyers' shipping schedule.
export default function PurchaseIncoming({ today, markets, lines }: Props) {
  const [query, setQuery] = React.useState("")
  const shown = lines.filter((line) => matchesSearch(line, query))
  const marketsById = React.useMemo(() => new Map(markets.map((market) => [market.id, market])), [markets])

  return (
    <AppLayout
      header={
        <PageTitle
          title="Incoming / PO"
          subtitle="Order lines on their way, from the buyers' shipping schedule. Producing and Shipping lines count toward stock; Pending lines do not yet. All markets."
        />
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          className="max-w-xs"
          placeholder="Search order, product or SKU"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        <div className="rounded-lg border bg-card">
          {shown.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm">Nothing on its way.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {COLUMNS.map((label) => (
                    <TableHead key={label} className={`${HEAD} ${label === "Qty" ? "text-right" : ""}`}>{label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((line) => (
                  <IncomingOrderRow
                    key={line.id}
                    line={line}
                    market={line.market_id ? marketsById.get(line.market_id) : undefined}
                    today={today}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
